/**
 * ToolsPage Component
 *
 * Main tools directory page with category filtering, search, and tool grid.
 * Displays all available tools with premium/free indicators and routing.
 */

import React from "react";
import { useNavigate } from "react-router-dom";

import { PageSEO } from "@/components/SEO/PageSEO";

export interface Tool {
  id: string;
  title: string;
  description: string;
  category: string;
  isPremium: boolean;
  icon: string;
  route?: string;
  comingSoon?: boolean;
}

interface ToolCardProps {
  tool: Tool;
  onClick: (tool: Tool) => void;
}

// Simple ToolCard component for the tools page
const ToolCard: React.FC<ToolCardProps> = ({ tool, onClick }) => {
  return (
    <div
      onClick={() => onClick(tool)}
      className={`
        relative bg-white rounded-2xl shadow-lg p-8 transition-all duration-300 cursor-pointer
        hover:shadow-2xl hover:scale-105 group border-2 border-transparent hover:border-[#B87333]
        ${tool.comingSoon ? "opacity-75" : ""}
      `}
    >
      {/* Premium Badge */}
      {tool.isPremium && (
        <div className="absolute top-4 right-4">
          <span className="bg-gradient-to-r from-amber-400 to-amber-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
            Premium
          </span>
        </div>
      )}

      {/* Coming Soon Badge */}
      {tool.comingSoon && (
        <div className="absolute top-4 right-4">
          <span className="bg-slate-400 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
            Coming Soon
          </span>
        </div>
      )}

      {/* Icon */}
      <div className="text-6xl mb-6 transform group-hover:scale-110 transition-transform duration-300">
        {tool.icon}
      </div>

      {/* Content */}
      <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-[#B87333] transition-colors">
        {tool.title}
      </h3>

      <p className="text-slate-600 leading-relaxed mb-4">{tool.description}</p>

      {/* Category */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
          {tool.category}
        </span>

        {/* Arrow indicator */}
        <div className="text-[#B87333] opacity-0 group-hover:opacity-100 transform group-hover:translate-x-2 transition-all duration-300">
          →
        </div>
      </div>
    </div>
  );
};

const ToolsPage: React.FC = () => {
  const navigate = useNavigate();

  // Essential business tools for AI automation assessment
  const tools: Tool[] = [
    {
      id: "roi-calculator",
      title: "Investment Recovery Calculator",
      description:
        "Calculate how quickly your AI automation investment pays for itself based on employee time savings. See a visual timeline of your break-even point and long-term ROI.",
      category: "Financial Analysis",
      isPremium: false,
      icon: "💰",
      route: "/tools/roi-calculator",
    },
    {
      id: "readiness-test",
      title: "AI Readiness Assessment",
      description:
        "Take our comprehensive assessment to evaluate your organization's readiness for AI implementation. Get a detailed scorecard with recommendations.",
      category: "Assessment",
      isPremium: false,
      icon: "🎯",
      route: "/tools/readiness-assessment",
    },
    {
      id: "feasibility-calculator",
      title: "Project Feasibility Calculator",
      description:
        "Evaluate the technical and business feasibility of your AI automation project. Assess complexity, resource requirements, and success probability.",
      category: "Planning",
      isPremium: false,
      icon: "📊",
      route: "/tools/feasibility-calculator",
    },
  ];

  const handleToolClick = (tool: Tool) => {
    if (tool.route) {
      navigate(tool.route);
    }
  };

  return (
    <>
      <PageSEO
        title="Free Business Assessment Tools"
        description="Evaluate your AI automation opportunity with our free calculators. Calculate ROI, assess organizational readiness, and evaluate project feasibility before investing in AI automation."
        keywords="ROI calculator, AI readiness assessment, feasibility calculator, automation tools, business assessment, AI implementation planning, investment recovery calculator"
        ogImage="/og-image-tools.png"
      />

      <div className="min-h-screen bg-slate-50">
        <div className="max-w-[1600px] mx-auto px-6 py-12">
          {/* Header */}
          <div className="mb-12 text-center">
            <p className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-4">
              Free Business Assessment Tools
            </p>
            <h1 className="text-5xl font-black text-slate-900 leading-tight mb-6">
              Evaluate Your AI Automation Opportunity
            </h1>
            <p className="text-xl text-slate-600 max-w-[800px] mx-auto">
              Use our interactive calculators to assess ROI, organizational
              readiness, and project feasibility before investing in AI
              automation
            </p>
          </div>

          {/* Tools Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 max-w-[1400px] mx-auto">
            {tools.map((tool) => (
              <ToolCard key={tool.id} tool={tool} onClick={handleToolClick} />
            ))}
          </div>

          {/* Call to Action */}
          <div className="mt-16 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl shadow-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Need a Custom AI Solution?
            </h2>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
              Can't find the perfect tool for your needs? We can build custom AI
              solutions tailored specifically to your business requirements.
            </p>
            <button
              onClick={() =>
                window.open(
                  "https://calendar.notion.so/meet/aigeniusconsulting/793ez04oz7",
                  "_blank",
                )
              }
              className="bg-[#B87333] hover:bg-[#A67329] text-white px-10 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
            >
              Schedule a Consultation
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ToolsPage;
